import { FC, useCallback, useMemo } from "react";
import { Category } from "@/types/category";
import styles from "./SelectCategory.module.scss";
import { useQuery } from "@tanstack/react-query";
import api, { DataResponse } from "@/services/api";
import { CategoryCard } from "@/components/category-card";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { logFirebaseEvent } from "@/services/firebase";
import { playSound, SoundOption } from "@/helpers/playSound";
import { useAppSelector } from "@/hooks/useAppSelector";
import { makeSelectVisibleProductIds } from "@/store/product/selectors";

interface SelectCategoryProps {
  onCategorySelected: (category: Category) => void;
}

const SelectCategory: FC<SelectCategoryProps> = ({ onCategorySelected }) => {
  const { t, i18n } = useTranslation();
  const selectVisibleProductIds = useMemo(() => makeSelectVisibleProductIds(), []);
  const productIds = useAppSelector((state) =>
    selectVisibleProductIds(state, { sortLanguage: i18n.language })
  );
  const { data: categories } = useQuery(["categories"], api.categories.getAll, {
    select: useCallback(
      ({ data }: AxiosResponse<DataResponse<Category[]>>) =>
        data.data.filter(
          (category) =>
            category.productIds.filter((id) => productIds?.find((productId) => productId === id))
              .length > 0
        ),
      [productIds]
    ),
  });

  const onClick = (category: Category) => {
    playSound(SoundOption.Click1);
    onCategorySelected(category);
    logFirebaseEvent("category_card_pressed", {
      id: category.id,
      name: category.translations[0].name,
    });
  };

  if (!categories) return null;
  return (
    <>
      <h3 className="h4" data-cy="select-category-title">
        {t("mainPage.selectCategory")}
      </h3>
      <div className={styles.gridWrapper}>
        <div className={styles.grid}>
          {categories.map((category) => (
            <CategoryCard key={category.id} category={category} onClick={() => onClick(category)} />
          ))}
        </div>
      </div>
    </>
  );
};

export default SelectCategory;
